import * as React from "react";
import { debounce, flatten, values, isEqual } from "lodash";
import { useSelector } from "react-redux";

import { AnsweredQuestionWithId, QuestionnairesState } from "../model";
import { QuestionnaireContainerOwnProps } from "../QuestionnaireContainerProps";
import { toast } from "../../toasts";

import { isClinicalNotesBeingCompletedInAdvance } from "./models";

type AutoSaveArgs = Pick<
    QuestionnaireContainerOwnProps,
    "readonly" | "loadQuestionnaire" | "autoSaveQuestionnaire" | "submitQuestionnaire" | "questionnaireKey" | "task" | "removeAppointment"
>;

export const useAutoSave = ({
    readonly, loadQuestionnaire, autoSaveQuestionnaire, submitQuestionnaire, questionnaireKey, task, removeAppointment
}: AutoSaveArgs) => {
    const isMounted = React.useRef(true);
    React.useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);
    const [loaded, setLoaded] = React.useState(false);
    const [submitClicked, setSubmitClicked] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [hasQueuedSaveCall, setHasQueuedSaveCall] = React.useState(false);
    const [pendingQuestionChanges, setPendingQuestionChanges] = React.useState(false);
    const questionnaire = useSelector((state: { questionnaires: QuestionnairesState }) => state.questionnaires[questionnaireKey]);
    const questions = questionnaire ? flatten(questionnaire.sections.map(section => values(section.questions))) : undefined;
    const saveQuestionnaire = React.useCallback(async (questionsToSave: AnsweredQuestionWithId[], currentETag: string | null) => {
        if (autoSaveQuestionnaire === undefined) { return; }
        try {
            if (!readonly) {
                if (saving) {
                    setHasQueuedSaveCall(true);
                    return;
                }
                setSaving(true);
                await autoSaveQuestionnaire(questionsToSave, currentETag);
                setPendingQuestionChanges(false);
            }
        } catch (ex) {
            if (ex.status === 400) {
                loadQuestionnaire(new AbortController());
            } else {
                throw ex;
            }
        } finally {
            setSaving(false);
        }
    }, [autoSaveQuestionnaire, readonly, saving, loadQuestionnaire]);
    const saveDebounced = React.useMemo(() => debounce(saveQuestionnaire, 1500), [saveQuestionnaire]);
    React.useEffect(() => {
        const abortController: AbortController = new AbortController();
        (async () => {
            await loadQuestionnaire(abortController);
            setLoaded(true);
        })();
        return () => {
            abortController.abort();
            setLoaded(false);
        };
    // We can't add load here because many usages of this component pass in lambda functions
    // which will change on each render and thus trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const submit = React.useCallback(() => {
        setSubmitClicked(true);
        saveDebounced.flush();
    }, [saveDebounced]);

    React.useEffect(() => {
        if (submitClicked && saving === false && pendingQuestionChanges === false && hasQueuedSaveCall === false && questions) {
            (async () => {
                setSaving(true);
                try {
                    const submitted = await submitQuestionnaire(questions);

                    if (submitted) {
                        try {
                            if (task && removeAppointment && isClinicalNotesBeingCompletedInAdvance(task)) {
                                removeAppointment(task.caseId, task.id);
                            }
                        } catch {
                            toast.error("Something went wrong during removing the appointment");
                        }
                    }
                }
                finally {
                    if (isMounted.current) {
                        setSubmitClicked(false);
                        setSaving(false);
                    }
                }
            })();
        }
        // We can't add all dependencies here because many usages of this component pass in lambda functions
        // which will change on each render and thus trigger this effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving, submitClicked, pendingQuestionChanges]);
    const previousQuestions = React.useRef<AnsweredQuestionWithId[]>();
    React.useEffect(() => {
        if (autoSaveQuestionnaire === undefined || loaded === false || submitClicked === true) { return; }
        const questionsChanged = previousQuestions.current !== undefined && previousQuestions.current.some((q, i) => isEqual(q, questions![i]) === false);
        previousQuestions.current = questions;
        if (questionsChanged === true && saving === true) {
            setHasQueuedSaveCall(true);
        }
        else if (questionnaire?.eTag !== undefined
            && !readonly
            && (hasQueuedSaveCall || questionsChanged)
            && autoSaveQuestionnaire !== undefined
            && saving === false
            && questions) {
            setPendingQuestionChanges(true);
            saveDebounced(questions, questionnaire.eTag);
            setHasQueuedSaveCall(false);
        }
    }, [autoSaveQuestionnaire, hasQueuedSaveCall, loaded, questionnaire?.eTag, questions, readonly, saveDebounced, saving, submitClicked]);
    return { loaded, submit, saving, submitting: submitClicked };
};
